import { useState } from 'react';
import { User, Lock, Shield, Bell } from 'lucide-react';
import { Card, Button, Input } from '@/components/common';
import { useAuth } from '@/contexts/AuthContext';
import { profileService } from '@/services/profileService';
import { useToast } from '@/contexts/ToastContext';

export function SettingsPage() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    password: '',
    password_confirmation: '',
  });

  const handleUpdateProfile = async () => {
    setLoading(true);
    try {
      await profileService.updateProfile(profileData);
      showToast('Profile updated successfully', 'success');
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Failed to update profile', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.password !== passwordData.password_confirmation) {
      showToast('Passwords do not match', 'error');
      return;
    }
    setLoading(true);
    try {
      await profileService.changePassword(passwordData);
      showToast('Password changed successfully', 'success');
      setPasswordData({ current_password: '', password: '', password_confirmation: '' });
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Failed to change password', 'error');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'security', label: 'Security', icon: Lock },
    { id: 'mfa', label: 'Two-Factor Auth', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-secondary-900">Settings</h1>
        <p className="text-secondary-600 mt-1">Manage your account settings and preferences</p>
      </div>

      <div className="flex gap-6">
        {/* Tabs */}
        <div className="w-64 space-y-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                activeTab === tab.id
                  ? 'bg-primary-50 text-primary-700 font-medium'
                  : 'text-secondary-600 hover:bg-secondary-100'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1">
          {activeTab === 'profile' && (
            <Card>
              <h2 className="text-xl font-bold mb-4">Profile Information</h2>
              <div className="space-y-4">
                <Input
                  label="Name"
                  value={profileData.name}
                  onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  placeholder="Your name"
                />
                <Input
                  label="Email"
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                  placeholder="your@email.com"
                />
                <Button onClick={handleUpdateProfile} disabled={loading}>
                  Update Profile
                </Button>
              </div>
            </Card>
          )}

          {activeTab === 'security' && (
            <Card>
              <h2 className="text-xl font-bold mb-4">Change Password</h2>
              <div className="space-y-4">
                <Input
                  label="Current Password"
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  placeholder="Enter current password"
                />
                <Input
                  label="New Password"
                  type="password"
                  value={passwordData.password}
                  onChange={(e) => setPasswordData({ ...passwordData, password: e.target.value })}
                  placeholder="Enter new password"
                />
                <Input
                  label="Confirm New Password"
                  type="password"
                  value={passwordData.password_confirmation}
                  onChange={(e) => setPasswordData({ ...passwordData, password_confirmation: e.target.value })}
                  placeholder="Confirm new password"
                />
                <Button onClick={handleChangePassword} disabled={loading}>
                  Change Password
                </Button>
              </div>
            </Card>
          )}

          {activeTab === 'mfa' && (
            <Card>
              <h2 className="text-xl font-bold mb-4">Two-Factor Authentication</h2>
              <p className="text-secondary-600 mb-4">
                Add an extra layer of security to your account by enabling two-factor authentication.
              </p>
              <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-4">
                <p className="text-sm text-secondary-700">
                  MFA setup wizard coming soon. You'll be able to enable TOTP-based authentication using
                  apps like Google Authenticator or Authy.
                </p>
              </div>
            </Card>
          )}

          {activeTab === 'notifications' && (
            <Card>
              <h2 className="text-xl font-bold mb-4">Notification Preferences</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Email Notifications</p>
                    <p className="text-sm text-secondary-600">Receive email updates about your account</p>
                  </div>
                  <input type="checkbox" className="w-5 h-5 text-primary-600 rounded" defaultChecked />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Security Alerts</p>
                    <p className="text-sm text-secondary-600">Get notified about security events</p>
                  </div>
                  <input type="checkbox" className="w-5 h-5 text-primary-600 rounded" defaultChecked />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Login Notifications</p>
                    <p className="text-sm text-secondary-600">Alert when new login detected</p>
                  </div>
                  <input type="checkbox" className="w-5 h-5 text-primary-600 rounded" />
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
